/** Number formatting that reads the way a machinist writes on a traveller. */

/**
 * Decimal rounding that survives binary float representation.
 *
 * Naive `Math.round(v * 100) / 100` gets 1.005 wrong, because 1.005 * 100 is
 * 100.49999999999999 in IEEE 754. Normalising through toPrecision(15) first
 * discards the representation error before the rounding decision is made.
 */
export function roundTo(value: number, places: number): number {
  if (!Number.isFinite(value)) return value;
  const f = 10 ** places;
  const scaled = Number((value * f).toPrecision(15));
  return Math.round(scaled) / f;
}

export function fixed(value: number, places: number): string {
  return roundTo(value, places).toFixed(places);
}

/** 0.1250" — four places is the shop default for inch work. */
export function inches(value: number, places = 4): string {
  return `${fixed(value, places)}"`;
}

/** 38.10 mm */
export function mm(value: number, places = 2): string {
  return `${fixed(value, places)} mm`;
}

/** 14.04° */
export function deg(value: number, places = 2): string {
  return `${fixed(value, places)}°`;
}

/** Whole-number RPM — nobody dials in a fractional spindle speed. */
export function rpmLabel(value: number): string {
  return `${Math.round(value)} RPM`;
}

/** 6.11 IPM */
export function ipmLabel(value: number, places = 2): string {
  return `${fixed(value, places)} IPM`;
}

/** Trim trailing zeros for inline arithmetic: 3.820 -> 3.82, 764.0 -> 764 */
export function trim(value: number, places = 4): string {
  const s = fixed(value, places);
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

/** "5/8" */
export function fractionLabel(numerator: number, denominator: number): string {
  return `${numerator}/${denominator}`;
}

/** Signed thou, for "how much comes off": +0.0010" / -0.0005" */
export function signedInches(value: number, places = 4): string {
  const s = fixed(Math.abs(value), places);
  return `${value < 0 ? '−' : '+'}${s}"`;
}

/**
 * Tenths are how the shop actually says small numbers: 0.0005" is "five
 * tenths", meaning five ten-thousandths. This is a genuine stumbling block
 * and the app should speak the shop's language back to her.
 */
export function shopSpeak(inchValue: number): string {
  const tenths = roundTo(Math.abs(inchValue) * 10000, 1);
  if (tenths === 0) return 'zero';
  if (tenths < 10) return `${trim(tenths, 1)} ${tenths === 1 ? 'tenth' : 'tenths'}`;
  const thou = roundTo(Math.abs(inchValue) * 1000, 1);
  return `${trim(thou, 1)} ${thou === 1 ? 'thou' : 'thou'}`;
}
